<template>
  <div class="app-container" :class="{ 'dark-mode': isDarkMode }">
    <div class="top-bar">
      <router-link to="/" class="home-link">{{ $t('navigation.home') }}</router-link>
      <div class="top-bar-controls">
        <button class="theme-toggle" @click="toggleDarkMode">
          {{ isDarkMode ? '☀️' : '🌙' }}
        </button>
        <select v-model="currentLocale" @change="changeLocale" class="language-select">
          <option value="en">English</option>
          <option value="cs">Čeština</option>
        </select>
      </div>
    </div>
    <router-view></router-view>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()
const currentLocale = ref(locale.value)
const isDarkMode = ref(false)

onMounted(() => {
  // Load dark mode preference from localStorage
  const savedDarkMode = localStorage.getItem('darkMode')
  isDarkMode.value = savedDarkMode === 'true'
  
  // Apply initial dark mode class to body
  updateBodyClass()
})

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  localStorage.setItem('darkMode', isDarkMode.value)
  updateBodyClass()
}

const updateBodyClass = () => {
  document.body.classList.toggle('dark-mode', isDarkMode.value)
}

const changeLocale = () => {
  locale.value = currentLocale.value
  localStorage.setItem('locale', currentLocale.value)
}

// Watch for dark mode changes
watch(isDarkMode, updateBodyClass)
</script>

<style>
body {
  margin: 0;
  font-family: sans-serif;
  transition: background-color 0.3s ease, color 0.3s ease;
}

body.dark-mode {
  background-color: #1a1a1a;
  color: #ffffff;
}

.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s ease, color 0.3s ease;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.app-container.dark-mode {
  background-color: #1a1a1a;
  color: #ffffff;
}

.top-bar {
  background-color: #f5f5f5;
  padding: 0.5rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  transition: background-color 0.3s ease;
  width: 100%;
  box-sizing: border-box;
}

.dark-mode .top-bar {
  background-color: #2d2d2d;
  border-bottom-color: #444;
}

.top-bar-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.theme-toggle {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  font-size: 1.2rem;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.theme-toggle:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.dark-mode .theme-toggle:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.language-select {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  border: 1px solid #ccc;
  background-color: white;
  cursor: pointer;
  font-size: 0.9rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.dark-mode .language-select {
  background-color: #2d2d2d;
  color: #ffffff;
  border-color: #444;
}

.dark-mode .language-select option {
  background-color: #2d2d2d;
  color: #ffffff;
}

.language-select:hover {
  border-color: #666;
}

.home-link {
  color: inherit;
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s ease;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.home-link:hover {
  opacity: 0.8;
  background-color: rgba(0, 0, 0, 0.1);
}

.dark-mode .home-link:hover {
  background-color: rgba(255, 255, 255, 0.1);
}
</style>
